import React, {
  useEffect, useCallback, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { addLineItems } from '../../../slices/cart';
import styles from './BuyMoreSaveMore.module.scss';
import messages from './messages';
import Stepper from '../../../components/common/Stepper';
import VariantSelection from '../VariantSelection';
import { VARIANT_ATTRIBUTE_NAME_COLOR_CODE } from '../VariantSelection/constants';
import AnimationWrapper from '../../../components/common/AnimationWrapper';
import {
  priceToString,
} from '../../../utils';
import {
  findVariantByAttributeValues,
} from '../../../utils/variants';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import PrimaryButton from '../../../components/common/PrimaryButton';
import SecondaryButton from '../../../components/common/SecondaryButton';
import AnimatedProductCard from '../../../components/common/AnimatedProductCard';
import RichTextDisplay from '../../../components/RichTextDisplay/RichTextDisplay';
import { ITEM_LIST_IDS_TEALIUM } from '../../../utils/common/constants';
import { findSizeMatchedVariant } from '../../../utils/aov';

const BuyMoreSaveMore = ({
  title, description = {}, discountsTier, items,
}) => {
  const dispatch = useDispatch();
  const { selectedSKU, selectedSecondSKU } = useSelector((state) => state.pdp);
  const { isLoading } = useSelector((state) => ({
    isLoading: state.cart.status === 'loading',
  }));
  const {
    product: { variants: mainProductVariants },
  } = useDrupalSettings();
  const mainProductSelectedVariant = mainProductVariants?.find(
    (mainProductVariant) => mainProductVariant.sku === selectedSKU,
  );
  const mainProductSelectedVariantAttributes =
    mainProductSelectedVariant?.attributes?.filter(
      ({ name }) => name !== VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
    );

  const { formatMessage, formatNumber, locale } = useIntl();

  const steps = useMemo(() => {
    const maxVal = discountsTier.reduce((prev, cur) =>
      (cur.items > prev.items ? cur : prev),
    ).items;

    return [
      {
        topLabel: formatMessage(messages.oneItem),
        bottomLabel: formatMessage(messages.noDiscountMessage),
      },
      ...discountsTier.map((discount) => ({
        topLabel:
          discount.items !== maxVal
            ? formatMessage(messages.manyItems, { count: discount.items })
            : formatMessage(messages.maxItems, { count: discount.items }),
        bottomLabel: formatMessage(messages.discountBadge, {
          discountPercentage: discount.discountPercentage,
        }),
      })),
    ];
  }, [discountsTier, formatMessage]);

  const [calculatedItems, setCalculatedItems] = useState(items);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [currentSubtotal, setCurrentSubtotal] = useState({ centAmount: 0 });
  const [isCurrentSubTotal, setIsCurrentSubtotal] = useState(true);

  const stepOffset = selectedSecondSKU ? 1 : 0;

  const currentStep = selectedVariants.length + stepOffset;

  const getDiscountPercentage = useCallback(
    (step) =>
      discountsTier.find((tier) => tier.items - 1 === step)
        ?.discountPercentage ??
      discountsTier.find((tier) => tier.items - 1 === discountsTier.length)
        .discountPercentage,
    [discountsTier],
  );

  useEffect(() => {
    setIsCurrentSubtotal(false);
    const subTotal = selectedVariants.reduce(
      (acc, { sku: currSku }) => {
        const item = calculatedItems.find(
          (product) => !!product.variants.find(({ sku }) => sku === currSku),
        );

        const variant = item.variants.find(
          (iterVariant) => iterVariant.sku === currSku,
        );

        return {
          total: {
            ...variant.price,
            ...acc.total,
            centAmount: acc.total.centAmount + variant.price.centAmount,
          },
          discount: {
            ...variant.price,
            ...acc.discount,
            centAmount: acc.discount.centAmount + variant.discount?.centAmount,
          },
        };
      },
      {
        total: {
          centAmount: 0,
        },
        discount: {
          centAmount: 0,
        },
      },
    );

    setCurrentSubtotal(subTotal);
  }, [selectedVariants, items, calculatedItems]);

  useEffect(() => {
    if (!isCurrentSubTotal) setIsCurrentSubtotal(true);
  }, [isCurrentSubTotal]);

  useEffect(() => {
    const newItems = items.map((item) => ({
      ...item,
      variants: item.variants.map((variant) => {
        const isFixed = selectedVariants.find(({ sku }) => sku === variant.sku);

        const percentage = isFixed
          ? getDiscountPercentage(currentStep)
          : getDiscountPercentage(currentStep + 1);
        const discount = variant.price.centAmount * (percentage / 100);

        return {
          ...variant,
          price: {
            ...variant.price,
            centAmount: variant.price.centAmount - discount,
          },
          discount: {
            ...variant.discount,
            centAmount: discount,
          },
        };
      }),
    }));

    setCalculatedItems(newItems);
  }, [
    selectedVariants,
    selectedSKU,
    items,
    getDiscountPercentage,
    currentStep,
  ]);

  const toggleSelectItem = useCallback(
    (productUid) => {
      const product = items.find(({ productId }) => productId === productUid);
      const defaultVariant =
        product.variants.find((variant) => variant.masterVariant === true)
        ?? product.variants[0];

      const isVariantSelected =
        selectedVariants.find(({ productId }) => productId === productUid) !==
        undefined;

      const newSelectedVariants = isVariantSelected
        ? selectedVariants.filter(({ productId }) => productId !== productUid)
        : [
          ...selectedVariants,
          {
            productId: productUid,
            sku: defaultVariant.sku,
          },
        ];

      setSelectedVariants(newSelectedVariants);
    },
    [items, selectedVariants],
  );

  const selectVariantCallback = useCallback(
    ({ sku }) => {
      const productItem = items.find(
        (product) => !!product.variants.find((variant) => variant.sku === sku),
      );

      setSelectedVariants((prevState) => [
        ...prevState.filter(
          ({ productId }) => productId !== productItem.productId,
        ),
        { productId: productItem.productId, sku },
      ]);
    },
    [items],
  );

  const addItemsHandler = useCallback(async () => {
    await dispatch(
      addLineItems({
        lineItems: selectedVariants.map(({ sku }) => (
          { sku, quantity: 1,
            item_list_id: ITEM_LIST_IDS_TEALIUM.minicartAovDrivers,
          }
        )),
      }),
    );
    window.location.href = '/cart';
  }, [dispatch, selectedVariants]);

  const continueToCartHandler = () => {
    window.location.href = '/cart';
  };

  // Get current Discount Percentage
  const badgeText = getDiscountPercentage(currentStep);
  return (
    <div className={styles.parent}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles['content-body']}>
            <span className={styles.heading}>{title[locale]}</span>
            <RichTextDisplay content={description[locale]} />
            <div className={styles['stepper-container']}>
              <Stepper activeStep={currentStep} steps={steps} />
            </div>
            <div className={styles['products-container']}>
              {calculatedItems.map((item, index) => {
                const selectVariant = selectedVariants.find(
                  ({ productId }) => productId === item.productId,
                );

                const isSelected = !!selectVariant;

                const variant = !isSelected
                  ? (findSizeMatchedVariant(
                      calculatedItems[index]?.variants,
                      mainProductSelectedVariantAttributes,
                    ) ?? calculatedItems[index]?.variants[0])
                  : calculatedItems[index].variants.find(
                    (v) => v.sku === selectVariant.sku,
                  );

                return (
                  <div key={item.productId}>
                    <AnimatedProductCard
                      key={item.productId}
                      id={item.productId}
                      title={item.productName[locale]}
                      description={item.description[locale]}
                      name={item.productName[locale]}
                      price={variant.price}
                      discount={
                        variant.discount?.centAmount ? variant.discount : undefined
                      }
                      imageURL={variant.image}
                      isSelected={isSelected}
                      badgeStyle="alternative"
                      badge={
                        isSelected
                          ? formatMessage(messages.discountBadge, {
                            discountPercentage: badgeText,
                          }).toUpperCase()
                          : null
                      }
                      onChange={toggleSelectItem}
                      hasVariants={items[index].variants.length > 1}
                      displayDiscount={selectedVariants.length > 0}
                    />

                    <AnimationWrapper
                      isShowing={isSelected}
                      onShowClassName={styles['product-variants-wrapper-open']}
                      onHideClassName={styles['product-variants-wrapper-close']}
                    >
                      <VariantSelection
                        variants={calculatedItems[index]?.variants}
                        attributesExtraInfo={items[index].attributeLinks}
                        defaultSelectedSKU={
                          (
                            findSizeMatchedVariant(
                              calculatedItems[index]?.variants,
                              variant?.attributes ||
                                mainProductSelectedVariantAttributes,
                            ) ??
                            findVariantByAttributeValues({
                              variants: calculatedItems[index]?.variants,
                              attributeValues:
                                variant?.attributes ||
                                mainProductSelectedVariantAttributes,
                            })
                          )?.sku
                        }
                        isShowingPriceDetails
                        isShowingDiscountedPrices
                        onSelectVariant={selectVariantCallback}
                      />
                    </AnimationWrapper>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
      <div className={classnames(styles['buy-more-save-more-footer'], { [styles['has-selection']]: stepOffset < currentStep })}>
        <p
          className={classnames(styles['buy-more-save-more-footer-text'], {
            [styles['buy-more-save-more-footer-text-max']]:
              currentStep >= discountsTier.length,
          })}
        >
          {[...Array(discountsTier.length).keys()].map((tierStep) => (
            <AnimationWrapper
              isShowing={currentStep === tierStep}
              onHideClassName={styles.fadeOutFast}
              onShowClassName={styles.fadeIn}
            >
              <span>
                {formatMessage(messages.addAnotherItemSave, {
                  discountPercentage: getDiscountPercentage(currentStep + 1),
                })}
              </span>
            </AnimationWrapper>
          ))}
          <AnimationWrapper
            isShowing={currentStep >= discountsTier.length}
            onHideClassName={styles.fadeOutFast}
            onShowClassName={styles.fadeIn}
          >
            <span>
              {formatMessage(messages.maxDiscount, {
                discountPercentage: getDiscountPercentage(currentStep),
              })}
            </span>
          </AnimationWrapper>
        </p>

        <AnimationWrapper
          isShowing={currentStep > stepOffset}
          onHideClassName={styles.footerSlideIn}
          onShowClassName={styles.footerSlideOut}
        >
          <>
            <div className={styles.separator} />
            <div className={styles['buy-more-save-more-footer-total-price']}>
              {formatMessage(messages.totalPrice)}

              <div className={styles['buy-more-save-more-footer-subtotal']}>
                <AnimationWrapper
                  isShowing={isCurrentSubTotal}
                  onHideClassName={styles.fadeOutFast}
                  onShowClassName={styles.fadeIn}
                >
                  <span
                    className={
                      styles['buy-more-save-more-footer-subtotal-before']
                    }
                  >
                    {priceToString({
                      price: {
                        ...currentSubtotal.total,
                        centAmount:
                          currentSubtotal.total?.centAmount +
                          currentSubtotal.discount?.centAmount,
                      },
                      maximumFractionDigits: 0,
                      formatNumber,
                      formatMessage,
                    })}
                  </span>
                </AnimationWrapper>
                <AnimationWrapper
                  isShowing={isCurrentSubTotal}
                  onHideClassName={styles.fadeOutFast}
                  onShowClassName={styles.fadeIn}
                >
                  <span
                    className={
                      styles['buy-more-save-more-footer-subtotal-after']
                    }
                  >
                    {priceToString({
                      price: {
                        ...currentSubtotal.total,
                        centAmount: currentSubtotal.total?.centAmount,
                      },
                      maximumFractionDigits: 0,
                      formatNumber,
                      formatMessage,
                    })}
                  </span>
                </AnimationWrapper>
              </div>
            </div>
          </>

        </AnimationWrapper>
        <div className={styles['buy-more-save-more-footer-button']}>
          {currentStep > stepOffset ? (

            <PrimaryButton
              isLoading={isLoading}
              onClick={addItemsHandler}
              title={formatMessage(
                selectedVariants.length > 1
                  ? messages.addToCartMultipleItems
                  : messages.addToCartSingleItem,
              )}
            />

          ) : (

            <SecondaryButton
              onClick={continueToCartHandler}
              title={formatMessage(messages.continueToCart)}
            />

          )}
        </div>
      </div>
    </div>
  );
};

BuyMoreSaveMore.propTypes = {
  title: PropTypes.object.isRequired,
  description: PropTypes.object,
  discountsTier: PropTypes.arrayOf(PropTypes.object).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default BuyMoreSaveMore;
